import React, {useState} from 'react'
import styled from 'styled-components'
import DialogTitle from '@material-ui/core/DialogTitle'
import DialogContent from '@material-ui/core/DialogContent'
import Dialog from 'lib/ui/Dialog'
import Select from 'lib/ui/Select'
import Option from 'lib/ui/Select/Option'
import {Label} from 'lib/ui/typography'
import {onUnknownChangeHandler} from 'lib/dom'
import {Certificate} from 'lib/event-api/certificates/types'
import {useCertificates} from 'lib/event-api/certificates/use-certificates'
import Button from 'lib/ui/Button'
import {useAttendees} from 'organization/Event/AttendeesProvider'
import {useToggle} from 'lib/toggle'

interface SendCertificateDialogProps {
  isVisible: boolean
  onClose: () => void
  onSuccess: (message: string | null) => void
  onError: (message: string | null) => void
}

export default function SendCertificateDialog(
  props: SendCertificateDialogProps,
) {
  if (!props.isVisible) {
    return null
  }

  return <Content {...props} />
}

function Content(props: SendCertificateDialogProps) {
  const [certificateId, setCertificateId] = useState<number | null>()
  const {data: certificates = []} = useCertificates()
  const {sendCertificate, processing} = useSendCertificate(props.onSuccess)

  const selectCertificate = (id?: number) => {
    if (id === undefined) {
      return
    }

    setCertificateId(id || null)
  }

  const send = () => {
    const certificate = certificates.find((c) => c.id === certificateId)
    if (!certificate) {
      return
    }

    sendCertificate(certificate)
    props.onClose()
  }

  return (
    <Dialog open={props.isVisible} onClose={props.onClose}>
      <DialogTitle>Send Certificate</DialogTitle>
      <DialogContent>
        <StyledLabel>Select Certificate to Send</StyledLabel>
        <Select
          fullWidth
          onChange={onUnknownChangeHandler(selectCertificate)}
          aria-label="pick certificate"
        >
          {certificates.map((c) => (
            <Option key={c.id} value={c.id} aria-label={`pick ${c.title}`}>
              {c.title}
            </Option>
          ))}
        </Select>
        <Button
          variant="contained"
          color="primary"
          fullWidth
          onClick={send}
          aria-label="send certificate"
          disabled={!certificateId || processing}
        >
          Send Certificate
        </Button>
      </DialogContent>
    </Dialog>
  )
}

const StyledLabel = styled(Label)`
  margin-bottom: ${(props) => props.theme.spacing[4]}!important;
`

export function useSendCertificate(
  onSuccess: (message: string | null) => void,
) {
  const {flag: processing, toggle: toggleProcessing} = useToggle()
  const {sendCertificate, clearError} = useAttendees()

  const process = (certificate: Certificate) => {
    if (processing) {
      return
    }

    toggleProcessing()
    onSuccess(null)
    clearError()

    sendCertificate(certificate)
      .then(() => {
        onSuccess(
          "Send Certificate request received.  We'll send a download link to the attendees specified.",
        )
      })
      .finally(toggleProcessing)
  }

  return {
    processing,
    sendCertificate: process,
  }
}
